export const isFunctionNative = (fn) => {
    const re =
        /^\s*function\s*(\b[a-z$_][a-z0-9$_]*\b)*\s*\((|([a-z$_][a-z0-9$_]*)(\s*,[a-z$_][a-z0-9$_]*)*)\)\s*{\s*\[native code\]\s*}\s*$/i;
    return (
        !!fn &&
        (typeof fn).toLowerCase() == 'function' &&
        (fn === Function.prototype || re.test(String(fn)))
    );
};

const _fallbackUserMedia =
    navigator?.getUserMedia || navigator?.webkitGetUserMedia || navigator?.mozGetUserMedia;

export const isDefinedNavigator = (props?: string[]) => {
    let defined = typeof globalThis.navigator !== 'undefined';
    if (defined && Array.isArray(props)) {
        defined = props.every((prop) => prop in globalThis.navigator);
    }
    return defined;
};

export const requestBrowserPermission = (permissionDesc: {
    name: ManualPermissionName;
}): Promise<PermissionStatus> => {
    try {
        if (navigator?.permissions?.query) {
            return navigator.permissions.query(permissionDesc as PermissionDescriptor);
        }
    } catch (e) {
        return Promise.reject(e);
    }
    return Promise.reject(new Error('it is not possible to request permissions in the browser'));
};

export const requestBrowserEnumerateDevices = (): Promise<MediaDeviceInfo[]> => {
    try {
        if (navigator?.mediaDevices?.enumerateDevices) {
            return navigator.mediaDevices.enumerateDevices();
        }
    } catch (e) {
        return Promise.reject(e);
    }
    return Promise.resolve<MediaDeviceInfo[]>([]);
};

export const requestBrowserUserMedia = (
    constraints?: MediaStreamConstraints,
): Promise<MediaStream> => {
    try {
        if (navigator?.mediaDevices?.getUserMedia) {
            return navigator.mediaDevices.getUserMedia(constraints);
        }

        if (isDefinedNavigator() && _fallbackUserMedia) {
            return new Promise((resolve, reject) => {
                const fn = _fallbackUserMedia.bind(navigator);
                fn(
                    constraints,
                    (stream) => resolve(stream),
                    (error) => reject(error),
                );
            });
        }
    } catch (e) {
        return Promise.reject(e);
    }
    return Promise.reject(new Error('it is not possible to request user media in the browser'));
};

export const isIOSBrowser = () =>
    !Object.is(globalThis?.navigator?.userAgent?.match(/ipad|ipod|iphone/i), null);

export const isIOSDevice = () =>
    globalThis?.navigator?.userAgent?.match(/ipad|ipod|iphone/i) || false;

export const isIOSHybrid = () =>
    globalThis?.navigator?.userAgent?.indexOf('VtbOnlineIosHybrid') > -1;

export const isAndroidBrowser = () =>
    !Object.is(globalThis?.navigator?.userAgent?.match(/android/i), null);

export const getIsAndroidHybrid = () =>
    globalThis?.navigator?.userAgent?.indexOf('VtbOnlineAndroidHybrid') > -1;

export const isLunaBrowser = () =>
    !Object.is(globalThis?.navigator?.userAgent?.match(/sapphire/i), null);

export const isOnVtbRu = () =>
    !Object.is(globalThis?.location?.hostname.match(/www.vtb.ru/i), null);

export const isMacOs = () => globalThis?.navigator?.userAgent?.indexOf('Mac OS X') != -1;

export const isLunaIOSBrowser = () => isLunaBrowser() && isIOSBrowser();

export const isLunaAndroidBrowser = () => isLunaBrowser() && isAndroidBrowser();

export const isSafariBrowser = () =>
    /^((?!chrome|android).)*safari/i.test(globalThis?.navigator?.userAgent);

export const getIsIOS = () =>
    Boolean(
        (globalThis?.currentPlatform ?? '') === 'ios' ||
            isIOSBrowser() ||
            isIOSDevice() ||
            isIOSHybrid() ||
            isLunaAndroidBrowser() ||
            /^((?!chrome|android).)*safari/i.test(navigator.userAgent),
    );

export const getIsPWA = () => {
    const match =
        window.matchMedia('(display-mode: standalone)').matches ||
        (window.navigator as unknown as Record<'standalone', unknown>).standalone;

    return match && getIsIOS();
};

export const getIsAndroid = () => isAndroidBrowser() || getIsAndroidHybrid();

export const isMobileDevice = () =>
    /Mobi|Android|iPhone|iPad|iPod|BlackBerry|Opera Mini|IEMobile/i.test(navigator.userAgent);
